<script setup>
// Porteira de troca de senha, para quem recebeu uma senha definida pelo administrador.
//
// ⚠️ NÃO PEDE A SENHA ATUAL, de propósito. Ela acabou de ser definida por um administrador e
// combinada de viva voz; exigi-la de volta não prova nada sobre quem está na frente da tela (a
// sessão já está autenticada) e trava justamente o caso real, que é a pessoa ter anotado errado
// ou recebido por telefone. A trava que importa está no servidor: esta troca só funciona
// enquanto a marca de troca obrigatória existir.
//
// ⚠️ ISTO É PORTEIRA DE TELA, e é honesto dizer onde ela para: cobre o painel inteiro e não deixa
// usar o sistema antes da troca, mas a API continua respondendo a quem fizer chamada direta.
// Bloquear de verdade exigiria mexer na autenticação do upstream, que serve o produto inteiro.
//
// Não tem botão de fechar. Sair daqui é trocar a senha, ou sair do sistema.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import TrocaSenhaAPI from 'dashboard/api/fandata/trocaSenha';
import Button from 'dashboard/components-next/button/Button.vue';

const CHAVE = 'fandata_trocar_senha';

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');

const precisaTrocar = computed(() =>
  Boolean(currentUser.value?.custom_attributes?.[CHAVE])
);

const nova = ref('');
const confirmacao = ref('');
const salvando = ref(false);

// A regra, item a item, conferida enquanto a pessoa digita. Dizer "senha inválida" depois de
// enviar é a pior forma de ensinar uma regra: ela acerta por tentativa e erro.
const requisitos = computed(() => [
  { chave: 'TAMANHO', ok: nova.value.length >= 6 },
  { chave: 'MAIUSCULA', ok: /[A-Z]/.test(nova.value) },
  { chave: 'MINUSCULA', ok: /[a-z]/.test(nova.value) },
  { chave: 'NUMERO', ok: /[0-9]/.test(nova.value) },
  { chave: 'ESPECIAL', ok: /[^A-Za-z0-9]/.test(nova.value) },
]);

const todosOsRequisitos = computed(() => requisitos.value.every(r => r.ok));
const combinam = computed(
  () => nova.value.length > 0 && nova.value === confirmacao.value
);
const podeSalvar = computed(
  () => todosOsRequisitos.value && combinam.value && !salvando.value
);

const trocar = async () => {
  if (!podeSalvar.value) return;
  salvando.value = true;
  try {
    const { data } = await TrocaSenhaAPI.trocar(nova.value);
    // Atualiza o usuário no painel: é o que faz a marca sumir e a porteira fechar sozinha.
    store.commit('SET_CURRENT_USER', data);
    useAlert(t('TROCA_SENHA.SUCESSO'));
  } catch (error) {
    const resposta = error?.response?.data;
    useAlert(resposta?.error || resposta?.message || t('TROCA_SENHA.ERRO'));
  } finally {
    salvando.value = false;
    nova.value = '';
    confirmacao.value = '';
  }
};

const sair = () => store.dispatch('logout');
</script>

<template>
  <div
    v-if="precisaTrocar"
    class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-background/95"
  >
    <div
      class="w-full max-w-md p-6 border shadow-xl rounded-xl border-n-weak bg-n-solid-1"
    >
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ $t('TROCA_SENHA.TITULO') }}
      </h2>
      <p class="mt-1 mb-4 text-sm text-n-slate-11">
        {{ $t('TROCA_SENHA.SUBTITULO') }}
      </p>

      <form class="flex flex-col gap-3" @submit.prevent="trocar">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('TROCA_SENHA.NOVA') }}
          </span>
          <input
            v-model="nova"
            type="password"
            autocomplete="new-password"
            class="mb-0"
          />
        </label>

        <!-- A regra conferida enquanto digita, item a item. -->
        <ul class="flex flex-col gap-0.5 mt-1 mb-1">
          <li
            v-for="r in requisitos"
            :key="r.chave"
            class="flex items-center gap-2 text-xs"
            :class="r.ok ? 'text-n-teal-11' : 'text-n-slate-11'"
          >
            <span
              class="size-3.5 shrink-0"
              :class="r.ok ? 'i-lucide-check' : 'i-lucide-dot'"
            />
            {{ $t(`TROCA_SENHA.REQUISITO.${r.chave}`) }}
          </li>
        </ul>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('TROCA_SENHA.CONFIRMAR') }}
          </span>
          <input
            v-model="confirmacao"
            type="password"
            autocomplete="new-password"
            class="mb-0"
          />
          <span
            v-if="confirmacao.length > 0 && !combinam"
            class="text-xs text-n-ruby-11"
          >
            {{ $t('TROCA_SENHA.NAO_COMBINAM') }}
          </span>
        </label>

        <div class="flex items-center justify-between gap-2 mt-2">
          <button
            type="button"
            class="text-sm underline text-n-slate-11 hover:text-n-slate-12"
            @click="sair"
          >
            {{ $t('TROCA_SENHA.SAIR') }}
          </button>
          <Button
            type="submit"
            :label="$t('TROCA_SENHA.SALVAR')"
            :is-loading="salvando"
            :disabled="!podeSalvar"
          />
        </div>
      </form>
    </div>
  </div>
</template>
